/**
 * Shared app-wide constants used across web and mobile platforms
 */

// ===== BRANDING CONSTANTS =====
export const APP_NAME = "Humble";
export const APP_DESCRIPTION = "Letterboxd for live events";
export const APP_TAGLINE = "Letterboxd for live events"; // Alias for description

// ===== DEFAULT LIMITS =====
export const DEFAULT_PAGE_SIZE = 20;
export const DEFAULT_FESTIVAL_LIMIT = 8;
export const DEFAULT_SHOW_LIMIT = 20;
export const DEFAULT_ARTIST_LIMIT = 20;

// ===== IMAGE ASPECT RATIOS =====
export const IMAGE_RATIOS = {
	FESTIVAL_POSTER: 3 / 4, // 3:4 (portrait)
	SHOW_POSTER: 1, // 1:1 (square)
	ARTIST_PHOTO: 1, // 1:1 (square)
} as const;

// Legacy exports for compatibility
export const FESTIVAL_POSTER_RATIO = IMAGE_RATIOS.FESTIVAL_POSTER;
export const SHOW_POSTER_RATIO = IMAGE_RATIOS.SHOW_POSTER;
export const ARTIST_PHOTO_RATIO = IMAGE_RATIOS.ARTIST_PHOTO;

// ===== SOCIAL MEDIA LINKS =====
export const SOCIAL_LINKS = {
	instagram: "https://www.instagram.com/humblefestivals/",
	tiktok: "https://www.tiktok.com/@humblefestivals",
	ios: "https://apps.apple.com/us/app/humble-festivals-with-friends/id6749406367",
	android: "https://play.google.com/store/apps/details?id=com.humbleos.mobile&hl=en_US",
} as const;

// ===== VALIDATION CONSTANTS =====
export const VALIDATION_CONSTANTS = {
	USERNAME: {
		MIN_LENGTH: 2,
		MAX_LENGTH: 30,
		PATTERN: /^[a-zA-Z0-9_]+$/,
		RESERVED_WORDS: [
			"admin",
			"api",
			"www",
			"mail",
			"ftp",
			"localhost",
			"root",
			"support",
			"help",
			"info",
			"contact",
			"about",
			"terms",
			"privacy",
			"legal",
			"blog",
			"news",
			"press",
			"careers",
			"humble",
			"festival",
			"festivals",
			"event",
			"events",
		],
	},
	PASSWORD: {
		MIN_LENGTH: 8,
		PATTERNS: {
			UPPERCASE: /[A-Z]/,
			LOWERCASE: /[a-z]/,
			NUMBER: /\d/,
			SPECIAL_CHAR: /[!@#$%^&*(),.?":{}|<>]/,
		},
	},
	EMAIL: {
		PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
		DISPOSABLE_DOMAINS: [
			"10minutemail.com",
			"guerrillamail.com",
			"mailinator.com",
			"temp-mail.org",
			"throwaway.email",
		],
	},
} as const;

// ===== PAGINATION CONSTANTS =====
export const PAGINATION_CONSTANTS = {
	DEFAULT_PAGE_SIZE: 20,
	MAX_PAGE_SIZE: 100,
	INFINITE_SCROLL_THRESHOLD: 3,
} as const;

// ===== CALENDAR CONSTANTS =====
export const CALENDAR_CONSTANTS = {
	DAYS_OF_WEEK: [
		"Sunday",
		"Monday",
		"Tuesday",
		"Wednesday",
		"Thursday",
		"Friday",
		"Saturday",
	] as const,
	DAYS_OF_WEEK_SHORT: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const,
	MONTHS: [
		"January",
		"February",
		"March",
		"April",
		"May",
		"June",
		"July",
		"August",
		"September",
		"October",
		"November",
		"December",
	] as const,
	MONTHS_SHORT: [
		"Jan",
		"Feb",
		"Mar",
		"Apr",
		"May",
		"Jun",
		"Jul",
		"Aug",
		"Sep",
		"Oct",
		"Nov",
		"Dec",
	] as const,
} as const;

// ===== REVIEW & RATING CONSTANTS =====
export const RATING_CONSTANTS = {
	// Rating scale: 0-10 internally, displayed as 0-5 stars
	MIN_RATING: 0,
	MAX_RATING: 10,
	STAR_COUNT: 5,
	// Special values
	UNSET_RATING: -2, // Used in database for "not set"
	NO_RATING: 0, // Explicitly no rating given
	// Conversion helpers
	STARS_TO_RATING_MULTIPLIER: 2, // Convert 5-star to 10-point scale
	// Festival review specific
	FESTIVAL_RATING_CATEGORIES: [
		"overall",
		"vibe", 
		"production",
		"food",
		"logistics",
	] as const,
} as const;